import React, { useState, useEffect } from 'react';
import { Settings2, ChevronDown, ChevronUp, Globe, Clock, AlertCircle, Mail } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INPUT = "w-full px-3 py-2 text-sm border border-stone-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-stone-900/20";
const LABEL = "block text-xs font-medium text-stone-600 mb-1";

const DAYS = [
    { value: 0, label: 'Mon' }, { value: 1, label: 'Tue' }, { value: 2, label: 'Wed' },
    { value: 3, label: 'Thu' }, { value: 4, label: 'Fri' }, { value: 5, label: 'Sat' }, { value: 6, label: 'Sun' },
];

const TIMEZONE_OPTIONS = [
    { value: 'UTC', label: 'UTC (Coordinated Universal Time)' },
    { value: 'America/New_York', label: 'Eastern Time (New York)' },
    { value: 'America/Chicago', label: 'Central Time (Chicago)' },
    { value: 'America/Denver', label: 'Mountain Time (Denver)' },
    { value: 'America/Los_Angeles', label: 'Pacific Time (Los Angeles)' },
    { value: 'America/Sao_Paulo', label: 'São Paulo (BRT)' },
    { value: 'Europe/London', label: 'London (GMT/BST)' },
    { value: 'Europe/Paris', label: 'Paris / Berlin (CET)' },
    { value: 'Asia/Dubai', label: 'Dubai (GST)' },
    { value: 'Asia/Kolkata', label: 'India (IST)' },
    { value: 'Asia/Singapore', label: 'Singapore (SGT)' },
    { value: 'Asia/Manila', label: 'Manila (PHT)' },
    { value: 'Asia/Tokyo', label: 'Tokyo (JST)' },
    { value: 'Asia/Shanghai', label: 'China (CST)' },
    { value: 'Australia/Sydney', label: 'Sydney (AEST)' },
    { value: 'Pacific/Auckland', label: 'Auckland (NZST)' },
];

function convertHourToLocal(hour, fromTz) {
    try {
        const userTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
        if (fromTz === userTz) return null;
        const now = new Date();
        const srcDate = new Date(now.toLocaleString('en-US', { timeZone: fromTz }));
        const localDate = new Date(now.toLocaleString('en-US', { timeZone: userTz }));
        const offsetMs = localDate - srcDate;
        const result = new Date(2024, 0, 15, hour, 0);
        result.setTime(result.getTime() + offsetMs);
        return result.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
    } catch { return null; }
}

function getUserTzLabel() {
    try {
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
        return tz.replace(/_/g, ' ').split('/').pop();
    } catch { return 'local'; }
}

export default function WorkflowSettings({ settings = {}, onChange }) {
    const [expanded, setExpanded] = useState(false);
    const [senders, setSenders] = useState([]);
    const [systemEmail, setSystemEmail] = useState('');
    const [loadingSenders, setLoadingSenders] = useState(false);

    useEffect(() => {
        // Fetch senders on mount (for collapsed badge) or on expand
        if (senders.length === 0 && !loadingSenders) {
            setLoadingSenders(true);
            const token = localStorage.getItem('crm_token');
            axios.get(`${API}/campaigns/org-senders`, {
                headers: token ? { Authorization: `Bearer ${token}` } : {}
            })
                .then(res => {
                    const data = res.data || {};
                    // API returns {senders: [], system_email: "..."} or legacy array
                    if (Array.isArray(data)) {
                        setSenders(data);
                    } else {
                        setSenders(data.senders || []);
                        if (data.system_email) setSystemEmail(data.system_email);
                    }
                })
                .catch(() => {})
                .finally(() => setLoadingSenders(false));
        }
    }, []);

    const update = (key, value) => onChange({ ...settings, [key]: value });
    const updateBizHours = (key, value) => {
        const biz = { ...(settings.business_hours || { start: 9, end: 17, days: [0, 1, 2, 3, 4] }) };
        biz[key] = value;
        update('business_hours', biz);
    };

    const biz = settings.business_hours || { start: 9, end: 17, days: [0, 1, 2, 3, 4] };
    const tz = settings.execution_timezone || 'UTC';
    const tzLabel = TIMEZONE_OPTIONS.find(o => o.value === tz)?.label || tz;

    return (
        <div className="bg-white border-b border-stone-200">
            <button onClick={() => setExpanded(!expanded)}
                className="w-full px-4 py-2.5 flex items-center justify-between hover:bg-stone-50 transition-colors">
                <div className="flex items-center gap-2">
                    <Settings2 className="w-4 h-4 text-stone-500" />
                    <span className="text-sm font-medium text-stone-700">Workflow Settings</span>
                    {!expanded && (
                        <>
                            {(settings.sender_mode && settings.sender_mode !== 'system') && (
                                <span className="flex items-center gap-1 text-[10px] text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full">
                                    <Mail className="w-3 h-3" />
                                    {settings.sender_mode === 'enrolling_user' ? 'Enrolling user' :
                                     settings.sender_mode === 'specific_user' && senders.length > 0
                                        ? (senders.find(s => s.user_id === settings.send_as_user_id)?.name || 'Specific user')
                                        : 'Custom sender'}
                                </span>
                            )}
                            <span className="flex items-center gap-1 text-[10px] text-stone-400 bg-stone-50 px-2 py-0.5 rounded-full">
                                <Globe className="w-3 h-3" />
                                {tzLabel}
                            </span>
                        </>
                    )}
                </div>
                {expanded ? <ChevronUp className="w-4 h-4 text-stone-400" /> : <ChevronDown className="w-4 h-4 text-stone-400" />}
            </button>

            {expanded && (
                <div className="px-4 pb-4 space-y-4">
                    {/* Send From — Who emails come from */}
                    <div className="bg-amber-50 border border-amber-100 rounded-lg p-3 space-y-2">
                        <div className="flex items-center gap-2">
                            <Mail className="w-4 h-4 text-amber-600" />
                            <label className="text-xs font-semibold text-amber-900">Send Emails From</label>
                        </div>
                        {/* Sender Mode */}
                        <select
                            value={settings.sender_mode || 'system'}
                            onChange={e => {
                                const mode = e.target.value;
                                onChange({ ...settings, sender_mode: mode, send_as_user_id: mode === 'specific_user' ? (settings.send_as_user_id || null) : null });
                            }}
                            className={`${INPUT} border-amber-200 focus:ring-amber-500/20`}
                        >
                            <option value="system">Organisation System Email</option>
                            <option value="specific_user">Specific Team Member</option>
                            <option value="enrolling_user">Enrolling User (whoever adds the contact)</option>
                        </select>
                        {/* Specific user picker — only when mode is specific_user */}
                        {(settings.sender_mode === 'specific_user') && (
                            <select
                                value={settings.send_as_user_id || ''}
                                onChange={e => update('send_as_user_id', e.target.value || null)}
                                className={`${INPUT} border-amber-200 focus:ring-amber-500/20`}
                                disabled={loadingSenders}
                            >
                                <option value="">Select a team member...</option>
                                {loadingSenders ? (
                                    <option>Loading...</option>
                                ) : (
                                    senders.filter(s => s.user_id).map(s => (
                                        <option key={s.user_id} value={s.user_id}>
                                            {s.display}
                                        </option>
                                    ))
                                )}
                            </select>
                        )}
                        <p className="text-[10px] text-amber-700 leading-relaxed">
                            {settings.sender_mode === 'system' && `All emails sent from the organisation's default address (${systemEmail || 'system email'}).`}
                            {settings.sender_mode === 'specific_user' && 'All emails sent from the selected team member\'s email address.'}
                            {settings.sender_mode === 'enrolling_user' && 'Each contact\'s emails are sent from whoever enrolled them in the campaign.'}
                            {!settings.sender_mode && `All emails sent from the organisation's default address.`}
                        </p>
                    </div>

                    {/* Timezone — Primary Setting */}
                    <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 space-y-2">
                        <div className="flex items-center gap-2">
                            <Globe className="w-4 h-4 text-blue-600" />
                            <label className="text-xs font-semibold text-blue-900">Sequence Timezone</label>
                        </div>
                        <select value={tz} onChange={e => update('execution_timezone', e.target.value)}
                            className={`${INPUT} border-blue-200 focus:ring-blue-500/20`}>
                            {TIMEZONE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </select>
                        <p className="text-[10px] text-blue-700 leading-relaxed">
                            All times in this sequence are interpreted in this timezone. Email send windows, business hours, scheduled sends, and delay-until steps all use this timezone unless overridden at the step level.
                        </p>
                        {(() => {
                            const userTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
                            return userTz !== tz ? (
                                <p className="text-[10px] text-blue-500 mt-1">
                                    Your browser timezone: <strong>{getUserTzLabel()}</strong> ({userTz})
                                </p>
                            ) : (
                                <p className="text-[10px] text-blue-500 mt-1">
                                    This matches your browser timezone.
                                </p>
                            );
                        })()}
                    </div>

                    {/* Business Hours */}
                    <div>
                        <div className="flex items-center gap-1.5 mb-1.5">
                            <Clock className="w-3.5 h-3.5 text-stone-500" />
                            <label className="text-xs font-medium text-stone-600">Business Hours</label>
                            <span className="text-[10px] text-stone-400">({tzLabel})</span>
                        </div>
                        <div className="grid grid-cols-2 gap-2 mb-2">
                            <div>
                                <label className="text-[10px] text-stone-400">Start</label>
                                <select value={biz.start} onChange={e => updateBizHours('start', parseInt(e.target.value))} className={INPUT}>
                                    {Array.from({ length: 24 }, (_, i) => (
                                        <option key={i} value={i}>{String(i).padStart(2, '0')}:00</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="text-[10px] text-stone-400">End</label>
                                <select value={biz.end} onChange={e => updateBizHours('end', parseInt(e.target.value))} className={INPUT}>
                                    {Array.from({ length: 24 }, (_, i) => (
                                        <option key={i} value={i}>{String(i).padStart(2, '0')}:00</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                        <div className="flex gap-1">
                            {DAYS.map(d => (
                                <button key={d.value} type="button"
                                    onClick={() => {
                                        const days = biz.days || [];
                                        const newDays = days.includes(d.value) ? days.filter(x => x !== d.value) : [...days, d.value].sort();
                                        updateBizHours('days', newDays);
                                    }}
                                    className={`px-2 py-1 text-[10px] rounded font-medium transition-colors ${
                                        (biz.days || []).includes(d.value)
                                            ? 'bg-stone-900 text-white' : 'bg-stone-100 text-stone-500 hover:bg-stone-200'
                                    }`}>
                                    {d.label}
                                </button>
                            ))}
                        </div>
                        {(() => {
                            const localStart = convertHourToLocal(biz.start, tz);
                            const localEnd = convertHourToLocal(biz.end, tz);
                            return localStart && localEnd ? (
                                <p className="text-[10px] text-stone-400 mt-1.5">
                                    Your time ({getUserTzLabel()}): <strong className="text-stone-500">{localStart}</strong> – <strong className="text-stone-500">{localEnd}</strong>
                                </p>
                            ) : null;
                        })()}
                        <p className="text-[10px] text-stone-400 mt-1">Smart Delay steps will only execute within this window.</p>
                    </div>

                    {/* Re-enrollment */}
                    <div className="border-t border-stone-100 pt-3">
                        <label className={LABEL}>Re-enrollment Policy</label>
                        <select value={settings.re_enrollment || 'prevent'} onChange={e => update('re_enrollment', e.target.value)}
                            className={INPUT}>
                            <option value="prevent">Prevent re-enrollment</option>
                            <option value="allow">Allow re-enrollment anytime</option>
                            <option value="allow_after_completion">Allow after completion</option>
                        </select>
                    </div>

                    {settings.re_enrollment !== 'prevent' && (
                        <div>
                            <label className={LABEL}>Re-enrollment Delay (hours)</label>
                            <input type="number" min={0} value={settings.re_enrollment_delay_hours || 0}
                                onChange={e => update('re_enrollment_delay_hours', parseInt(e.target.value) || 0)}
                                className={INPUT} />
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
